import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import ArticlesListingTable, { ArticlesTitle, ArticleExcerpt } from './../../reusable/Articles/ArticlesListingTable';
import api from './ViewFeedContent.api';
import {DropdownFilter, DateCell, RiskLevel, TextCell} from "../../reusable/LightTable/Components";
import utils from './../../utils/utilities';
import reduxStore from './../../reduxStore';
import { SORT, RSS_FEED_PAGE_SIZE, LANGUAGE_SELECT_OPTIONS } from "../../constants";
import { FormattedMessage, injectIntl } from 'react-intl';
import { eq, merge, cloneDeep, groupBy, uniqBy } from 'lodash';
import commonActions from './../../api/Common.actions';

// For feeds we do things a bit different, we recursively load all of the pages and we override some of the
// ArticleListingTable and TableCore methods

class FeedArticlesTable extends ArticlesListingTable {
    static propTypes = {
        feedId: PropTypes.string,
        onClickArticle: PropTypes.func,
        onDataUpdate: PropTypes.func
    };

    constructor(props) {
        super(props);

        const language = reduxStore.getState().user.preferences.articlesLanguageFilter || "all";

        const defaultLoadParams = {
            filters: { feedId: props.feedId, language },
            sorting: { sortBy: "harvestDate", direction: SORT.DESC },
            pagination: { pageNumber: 0, pageSize: RSS_FEED_PAGE_SIZE },
            reset: false
        }

        this.state = {
            ...this.state,
            feedListItems: [],
        };

        this.defaultLoadParams = this.loadParams = cloneDeep(defaultLoadParams);

        this.api = api;
    }

    getLanguageFilter() {
        let options = [];
        Object.keys(LANGUAGE_SELECT_OPTIONS).forEach(langKey => {
            options.push({
                value: langKey,
                label: this.props.intl.formatMessage({ id: LANGUAGE_SELECT_OPTIONS[langKey]})
            });
        });

        return { field: "language", options, value: this.loadParams.filters.language };
    }

    onChangeLanguageFilter = (e) => {
        const language = e.target.value;
        commonActions.updateUserPrefs({articlesLanguageFilter: language});
        this.loadFeed({
            ...this.loadParams,
            pagination: { pageNumber: 0, pageSize: this.loadParams.pagination.pageSize },
            filters: { feedId: this.loadParams.filters.feedId, language }
        });
    }

    getHeaderItems() {
        return [
            { key: "title", label: <ArticlesTitle articleCount={this.state.totalCount} onRefresh={this.handleRefresh}/>},
            { key: "language", label: <DropdownFilter filter={this.getLanguageFilter()} onChange={this.onChangeLanguageFilter}/>},
            { key: "harvestDate", label: <FormattedMessage id="Entities_Preview_UIText_previewCompanies.date"/>, sortable: true },
            { key: "entityName", label: <FormattedMessage id={"Entities_Preview_UIText_previewCompanies.resultsPagination.entities"}/> },
            { key: "riskAngle", label: <FormattedMessage id={"Dashboard_Charts_UIText_dashboard.filters.riskAngle"}/>},
            { key: "riskLevel", label: <FormattedMessage id={"Dashboard_Charts_UIText_dashboard.filters.riskLevel"}/>}

        ]
    }

    componentDidMount() {
        this.loadFeed(this.defaultLoadParams);
    }

    loadFeed(loadParams) {
        this.setState({
            feedListItems: null,
            totalCount: null,
            filterCount: null
        });
        this.loadParams = merge({}, this.loadParams, loadParams); // mutates/merges the component loadParams
        return this.api.count(this.loadParams.filters).then(countRes => {
            this.setState({
                totalCount: countRes.body,
                filterCount: countRes.body,
                feedListItems: []
            }, () => {
                return this.loadFeedRecursively(this.loadParams);
            });
        })
    }

    loadFeedRecursively(loadParams) {
        if (loadParams.pagination.pageNumber > 100) return; // in case of page number goes wild
        if (this._hasSortFilterChange(loadParams)) return; // if sorting changes, cancel

        if (this.state.totalCount > this.state.feedListItems.length) {
            return api.get(loadParams.filters, loadParams.sorting, loadParams.pagination).then(items => {
                // check if load params changed while we were away doing the get call
                if (!this._hasSortFilterChange(loadParams)) {
                    this.setState({feedListItems: [...this.state.feedListItems, ...items.body]}, () => {
                        this.props.onDataUpdate(items.body, this.state.totalCount, this.state.filterCount)
                        this.loadFeedRecursively({
                            ...loadParams,
                            pagination: {...loadParams.pagination, pageNumber: loadParams.pagination.pageNumber + 1 }
                        })
                    });
                }
            })
        }
    }

    loadItems(params, retry) {
        // if sorting changes, restart the whole feed
        if (params.sorting && params.sorting.direction !== this.loadParams.sorting.direction) {
            this.loadFeed(params);
        }
        return super.loadItems(params, retry);
    }

    //overriding internal process, instead of calling the backend it just returns the totalCount from state, we already have it from loadFeed method
    _manageApiCountCall(filters) {
        return new Promise((resolve, reject) => {
            resolve(this.state.totalCount);
        });
    }

    // overriding internal process, basically blocking the call to get more items, we already have them cached in state.feedListItems
    _manageApiItemsCall(filters) {
        return new Promise(resolve => { resolve([]) });
    }

    _hasSortFilterChange = (loadParams) => !eq(this.loadParams.sorting, loadParams.sorting) || !eq(this.loadParams.filters, loadParams.filters);

    shouldRowUpdate(prevItem, nextItem) {
        return prevItem.object.feedId !== nextItem.object.feedId
    }

    handleArticleClick = (article) => (e) => {
        const { pageNumber, pageSize } = this.loadParams.pagination;
        const index = this.state.feedListItems.indexOf(article);
        this.props.onClickArticle(article, index);
    }

    formatItem(item, searchTerm) {
        let riskLevels = [], riskAngles = [];
        const risks = utils.getRiskSubjects(item.subjects);
        const groupedRisks = groupBy(risks, "riskFactor");
        Object.keys(groupedRisks).forEach(risk => {
            riskAngles.push(
                <div className={'risk-row'} key={"risk-angle-" + risk}>
                    <FormattedMessage id={"General_CoreFunctionality_UIText_general." + risk} />
                </div>
            );
            riskLevels.push(
                <div className={'risk-row'} key={"risk-level-" + risk}>
                    {uniqBy(groupedRisks[risk], "score").map(riskLevel =>
                        <RiskLevel key={"risk-bullet-"+riskLevel.score} risk={riskLevel}/>)}
                </div>
            );
        })
        return {
            title: <ArticleExcerpt title={item.title}
                                   licensed={item.licensed}
                                   subtitle={item.source}
                                   excerpt={item.content}
                                   onClick={this.handleArticleClick(item)}/>,
            language: item.language,
            harvestDate: <DateCell timestamp={item.harvestDate}/>,
            entityName: item.entityName,
            riskLevel: <Fragment>{riskLevels}</Fragment>,
            riskAngle: <Fragment>{riskAngles}</Fragment>,
            riskAngleLevel: <div className={'risk-angles-levels'}>
                <div className={'risk-angles'}>{riskAngles}</div>
                <div className={'risk-levels'}>{riskLevels}</div>
            </div>,
            object: item
        }
    }


    handleRefresh = () => {
        this.loadFeed({
            ...this.loadParams,
            pagination: { pageSize: this.loadParams.pagination.pageSize, pageNumber: 0 }
        });
    }

    // Overrides the method of how we get the listItems, basically returns a slice from the feedListItems cache for the current page
    getFormattedArticleItems() {
        const { pageNumber, pageSize } = this.loadParams.pagination;
        if (this.state.feedListItems !== null) {
            const items = this.state.feedListItems.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize).map(item => ({
                ...this.formatItem(item),
                updateFunc: [super.shouldRowUpdate, this.shouldRowUpdate]
            }));

            return items.length ? items : this.state.feedListItems.length === 0 ? [] : null;
        } else {
            return null;
        }
    }

    getTableProps() {
        return { ...super.getTableProps(), headerItems: this.getHeaderItems(), className: "article-listing-table feed-listing-table" }
    }
}

export default injectIntl(FeedArticlesTable);
//const LanguageFilter = <


// WEBPACK FOOTER //
// ./src/scripts/pages/ViewFeedContent/FeedArticlesTable.jsx